import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { T, FONT, MONO, radius, tint, alpha} from './poyais-ui/theme';
import { fmt_k } from './poyais-ui/format';
import { useIsMobile } from './poyais-ui/hooks';
import { Card, Grid, Eyebrow, Badge, Button } from './poyais-ui/primitives';
import { Icon } from './poyais-ui/icons';
import { exportCsv } from './poyais-ui/export';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Dışa Aktar': 'Export',
  'ChatGPT Ads Bağlı Değil': 'ChatGPT Ads Not Connected',
  "Ayarlar sayfasından OpenAI Ads API key'inizi girin.": 'Enter your OpenAI Ads API key on the Settings page.',
  'Ayarlara Git': 'Go to Settings',
  'Veri alınamadı': 'Could not load data',
  'Hata': 'Error',
  'HARCAMA': 'SPEND', 'TIKLAMA': 'CLICKS', 'GÖSTERİM': 'IMPRESSIONS', 'DURUM': 'STATUS',
  'önceki döneme göre': 'vs. previous period',
  'Günlük Trend': 'Daily Trend',
  'Tıklama': 'Clicks', 'Gösterim': 'Impressions', 'Harcama': 'Spend',
  'Tarih': 'Date',
  'Kampanyalar': 'Campaigns',
  'kampanya': 'campaigns',
  'Yeni Kampanya': 'New Campaign',
  'KAMPANYA': 'CAMPAIGN',
  'Bu dönem için kampanya yok.': 'No campaigns for this period.',
  'Aktif': 'Active', 'Durduruldu': 'Paused', 'Arşivlendi': 'Archived',
});

const API = '/api/v1';

function fmtN(v: number) { return Number(v || 0).toLocaleString('tr-TR'); }
function fmtPct(v: number) { return `%${Number(v || 0).toFixed(2)}`; }

function Loader() { return <div className="py-pageload"><PoyaisSpinner size={38} pad={0}/></div>; }

function DisconnectedState() { const { t } = useLang(); return (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 320, gap: 14, fontFamily: FONT }}><div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{t('ChatGPT Ads Bağlı Değil')}</div><div style={{ fontSize: 13, color: T.textDim }}>{t("Ayarlar sayfasından OpenAI Ads API key'inizi girin.")}</div><Button variant="solid" color={T.accent} onClick={() => window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'settings' }))}>{t('Ayarlara Git')}</Button></div>); }

// --- GENEL BAKIŞ ---
function OverviewTab({ clientId, start, end }: { clientId: number; start?: string; end?: string }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartMetric, setChartMetric] = useState<'clicks' | 'impressions' | 'spend' | 'ctr'>('clicks');
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  useEffect(() => {
    setLoading(true); setError(null); setData(null);
    axios.get(`${API}/chatgpt-ads/${clientId}/overview`, { params: { start_date: start || monthStart, end_date: end || today } })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.data?.detail || t('Veri alınamadı')); setLoading(false); });
  }, [clientId, start, end]);

  if (loading) return <Loader />;
  if (error) return <div style={{ background: tint(T.red, 8), border: `1px solid ${alpha(T.red, 25)}`, borderRadius: radius.lg, padding: '20px 24px', fontFamily: FONT }}><div style={{ fontSize: 13.5, fontWeight: 800, color: T.redText, marginBottom: 6 }}>{t('Hata')}</div><div style={{ fontSize: 12, color: T.textMid }}>{error}</div></div>;

  const daily = data?.daily || [];
  const changeColor = (v: number) => v > 0 ? T.greenText : v < 0 ? T.redText : T.textDim;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Grid min={isMobile ? 130 : 150} gap={isMobile ? 8 : 14}>
        {[
          { label: t('HARCAMA'), value: fmt_k(data?.spend || 0), change: data?.spend_change_pct, color: T.accent },
          { label: t('TIKLAMA'), value: fmtN(data?.clicks), change: data?.clicks_change_pct, color: T.blue },
          { label: t('GÖSTERİM'), value: fmtN(data?.impressions), change: data?.impressions_change_pct, color: T.textDim },
          { label: 'CTR', value: fmtPct(data?.ctr), color: T.textDim },
          { label: 'CPC', value: fmt_k(data?.cpc || 0), color: T.textDim },
          { label: 'CPM', value: fmt_k(data?.cpm || 0), color: T.textDim },
        ].map((k, i) => (
          <Card key={i} pad={18}>
            <Eyebrow color={k.color}>{k.label}</Eyebrow>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: MONO, fontVariantNumeric: 'tabular-nums', marginTop: 8 }}>{k.value}</div>
            {k.change !== undefined && (
              <div style={{ fontSize: 11, fontWeight: 700, color: changeColor(k.change), marginTop: 4 }}>
                {k.change > 0 ? '↑' : k.change < 0 ? '↓' : '·'} %{Math.abs(k.change)} {t('önceki döneme göre')}
              </div>
            )}
          </Card>
        ))}
      </Grid>
      {daily.length > 0 && (
        <Card pad={20}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' as const, gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{t('Günlük Trend')}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['clicks', 'impressions', 'spend', 'ctr'] as const).map(k => (
                <Button key={k} variant={chartMetric === k ? 'soft' : 'ghost'} color={T.accent} onClick={() => setChartMetric(k)}
                  style={{ padding: '4px 12px', fontSize: 10, fontWeight: chartMetric === k ? 700 : 400 }}>
                  {k === 'clicks' ? t('Tıklama') : k === 'impressions' ? t('Gösterim') : k === 'spend' ? t('Harcama') : 'CTR'}
                </Button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={daily} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={v => (v || '').slice(5)} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: T.textDim }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v: any) => chartMetric === 'spend' ? fmt_k(Number(v)) : chartMetric === 'ctr' ? fmtPct(Number(v)) : fmtN(Number(v))} labelFormatter={l => `${t('Tarih')}: ${l}`} />
              <Line type="monotone" dataKey={chartMetric} stroke={T.accent} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

// --- KAMPANYALAR ---
function CampaignsTab({ clientId, start, end }: { clientId: number; start?: string; end?: string }) {
  const { t } = useLang();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/chatgpt-ads/${clientId}/campaigns`, { params: { start_date: start || monthStart, end_date: end || today } })
      .then(r => { setData(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clientId, start, end]);

  if (loading) return <Loader />;
  const statusBadge = (s: string) => {
    if (s === 'active') return { text: t('Aktif'), color: T.greenText };
    if (s === 'paused') return { text: t('Durduruldu'), color: T.amberText };
    if (s === 'archived') return { text: t('Arşivlendi'), color: T.textDim };
    return { text: s || '—', color: T.textDim };
  };

  const handleExport = () => {
    exportCsv(`chatgpt-ads-kampanyalar-${start || ''}-${end || ''}.csv`, data, [
      { key: 'name', label: t('KAMPANYA') },
      { key: 'status', label: t('DURUM') },
      { key: 'spend', label: t('HARCAMA') },
      { key: 'impressions', label: t('GÖSTERİM') },
      { key: 'clicks', label: t('TIKLAMA') },
      { key: 'ctr', label: 'CTR' },
      { key: 'cpc', label: 'CPC' },
      { key: 'cpm', label: 'CPM' },
    ]);
  };

  return (
    <Card pad={0}>
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{t('Kampanyalar')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 11, color: T.textDim }}>{data.length} {t('kampanya')}</div>
          <Button variant="ghost" color={T.accent} disabled={data.length === 0} onClick={handleExport} style={{ padding: '6px 12px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon.download size={13} />{t('Dışa Aktar')}
          </Button>
          <Button variant="solid" color={T.accent}
            onClick={() => { localStorage.setItem('poyais_prefill_platform', 'chatgpt'); window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'poyaisone-campaign-builder' })); }}>
            {t('Yeni Kampanya')}
          </Button>
        </div>
      </div>
      <div style={{ overflowX: 'auto' as const }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, minWidth: 760 }}>
          <thead style={{ borderBottom: `1px solid ${T.border}`, background: T.card }}>
            <tr>
              {['KAMPANYA', 'DURUM', 'HARCAMA', 'GÖSTERİM', 'TIKLAMA', 'CTR', 'CPC', 'CPM'].map((h, i) => (
                <th key={i} style={{ padding: '10px 14px', fontSize: 9, fontWeight: 700, color: T.textDim, textTransform: 'uppercase' as const, letterSpacing: '0.08em', textAlign: i === 0 ? 'left' as const : 'right' as const, whiteSpace: 'nowrap' as const }}>{t(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '28px 14px', textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t('Bu dönem için kampanya yok.')}</td></tr>
            ) : data.map((c: any, i: number) => {
              const sb = statusBadge(c.status);
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${T.card}` }} onMouseEnter={e => (e.currentTarget.style.background = T.card)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ padding: '12px 14px' }}><div style={{ fontSize: 12, fontWeight: 600, color: T.text, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{c.name}</div></td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const }}><Badge color={sb.color}>{sb.text}</Badge></td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 12, fontWeight: 600, color: T.text, fontVariantNumeric: 'tabular-nums' }}>{fmt_k(c.spend)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 11, color: T.textMid, fontVariantNumeric: 'tabular-nums' }}>{fmtN(c.impressions)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 11, color: T.textMid, fontVariantNumeric: 'tabular-nums' }}>{fmtN(c.clicks)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 11, color: T.textMid, fontVariantNumeric: 'tabular-nums' }}>{fmtPct(c.ctr)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 11, color: T.textMid, fontVariantNumeric: 'tabular-nums' }}>{fmt_k(c.cpc)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 11, color: T.textMid, fontVariantNumeric: 'tabular-nums' }}>{fmt_k(c.cpm)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// --- MAIN EXPORT ---
export default function ChatGPTAdsTab({ config, tab = 'overview', start, end }: { config: any; tab?: string; start?: string; end?: string }) {
  const clientId = config?.client_id;
  const accountName = (config?.platforms as any)?.chatgpt_ads?.accountName || '';
  const enabled = (config?.platforms as any)?.chatgpt_ads?.enabled !== false;
  if (!config?.platforms) return <Loader />;
  if (!clientId || !accountName || !enabled) return <DisconnectedState />;
  if (tab === 'campaigns') return <CampaignsTab clientId={clientId} start={start} end={end} />;
  return <OverviewTab clientId={clientId} start={start} end={end} />;
}
